// lint-internal-leaks-ignore-next-line: framework-script — load-bearing visual regression marker
// @visual-diff-render: vite
/**
 * @file InfoRow — stat row
 * @version 0.1.1
 */

import React, { forwardRef, type ElementType, type HTMLAttributes } from 'react';
import styles from './InfoRow.module.css';

/*
 * InfoRow — a horizontal row of stats: a large numeral over an uppercase,
 * letter-spaced caption, items separated by a hairline left rule.
 *
 *   <InfoRow items={[{ number: '30 min', label: 'Average Duration' }, …]} />
 *
 * `theme` is a variant, not a global mode:
 *   light (default) — navy numeral, dark-blue caption, grey hairline.
 *   dark            — white numeral, grey caption, white hairline.
 *
 * Captions are uppercased in CSS — pass them in natural case. At narrow widths
 * the row stacks vertically.
 */

export type InfoRowTheme = 'light' | 'dark';

export interface InfoRowItem {
  /** Large display value, e.g. "30 min" or "1". */
  number: string;
  /** Caption below the number (rendered uppercase). */
  label: string;
}

export interface InfoRowProps extends HTMLAttributes<HTMLDivElement> {
  /** The stats, left → right. */
  items: readonly InfoRowItem[];
  /** Colour treatment. Default `light`. A variant, not a runtime theme mode. */
  theme?: InfoRowTheme;
  /** Render root as a different element (default `div`). */
  as?: ElementType;
}

export const InfoRow = forwardRef<HTMLDivElement, InfoRowProps>(
  function InfoRow({ items, theme = 'light', as, className, ...rest }, ref) {
    const Component = (as ?? 'div') as ElementType;
    const rootClass = [styles.root, styles[theme], className].filter(Boolean).join(' ');
    return (
      <Component ref={ref} className={rootClass} {...rest}>
        {items.map((s, i) => (
          <div key={`${i}-${s.label}`} className={styles.item}>
            <div className={styles.number}>{s.number}</div>
            <div className={styles.caption}>{s.label}</div>
          </div>
        ))}
      </Component>
    );
  },
);
